import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { AutomationFlowEntity } from './automation-flow.entity';
import { AutomationTriggerEntity } from './automation-trigger.entity';
import { BaseEntity } from './base.entity';

/**
 * A managed "Quote requests" intake: inbound customer emails read into
 * cross-reference requests, matched against the catalog, and optionally
 * enriched by external lookup.
 *
 * DECLARATIONS vs COMPILED ARTIFACTS. Every column above the compiled block is
 * a DECLARATION — what the user asked for. The two FK columns at the bottom are
 * OUTPUTS of the compile step (`email.received` → `email_ingest` →
 * `product_match` → `external_lookup`), regenerated on every save. They are
 * never user input, and nothing may write a declaration back from the compiled
 * flow: the flow is derived from this row, so a reverse write would create two
 * sources of truth that drift the first time somebody edits the flow directly.
 *
 * Run state (what happened, when, how it ended) deliberately lives on the
 * automation executions and on the requests those runs produced, and is read
 * through them. This row holds no counters and no last-run timestamp.
 *
 * JSON ARRAY COLUMNS ARE NEVER NULL. `emailSourceIds`, `fromDomains`,
 * `subjectContains`, `allowedDomains` and `blockedDomains` are stored as `[]`
 * when empty. An empty list and an absent list would otherwise read the same to
 * a caller while meaning different things to the filter logic, so the write
 * path normalizes to `[]` and readers may treat the value as always present.
 */
@Index('idx_cross_reference_intake_active', ['isActive'])
@Entity('cross_reference_intake')
export class CrossReferenceIntakeEntity extends BaseEntity {
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	/** Paused is `false`. A paused intake keeps every declaration it had. */
	@Column('boolean', { name: 'isActive', default: true })
	public isActive!: boolean;

	// === Declaration: which mailbox traffic to read ===

	/** `email_source` row ids to watch. Stored as `[]`, never null. */
	@Column('json', { name: 'emailSourceIds' })
	public emailSourceIds!: Array<number>;

	/** Sender domains to accept. Empty means "any sender". */
	@Column('json', { name: 'fromDomains' })
	public fromDomains!: Array<string>;

	/** Subject substrings to require. Empty means "any subject". */
	@Column('json', { name: 'subjectContains' })
	public subjectContains!: Array<string>;

	/** Skip emails with no attachment (most quote requests carry a document). */
	@Column('boolean', { name: 'requireAttachments', default: false })
	public requireAttachments!: boolean;

	// === Declaration: reading the email ===

	/** Null uses the engine default model. */
	@Column('varchar', { name: 'model', length: 64, nullable: true })
	public model!: string | null;

	/** Model the reader escalates to on a low-confidence pass. */
	@Column('varchar', { name: 'escalationModel', length: 64, nullable: true })
	public escalationModel!: string | null;

	@Column('varchar', { name: 'language', length: 16, nullable: true })
	public language!: string | null;

	/** USD cap per processed email. Null uses the engine default. */
	@Column('decimal', {
		name: 'spendBudget',
		precision: 10,
		scale: 4,
		nullable: true,
	})
	public spendBudget!: number | null;

	// === Declaration: matching ===

	/** Matching category the run is scored under. Null lets the engine pick. */
	@Column('varchar', { name: 'category', length: 128, nullable: true })
	public category!: string | null;

	/** Candidates kept per line. */
	@Column('int', { name: 'topN', unsigned: true, default: 5 })
	public topN!: number;

	// === Declaration: external lookup ===

	@Column('boolean', { name: 'lookupEnabled', default: true })
	public lookupEnabled!: boolean;

	/** Hostnames the lookup may cite. Empty means the engine default set. */
	@Column('json', { name: 'allowedDomains' })
	public allowedDomains!: Array<string>;

	/** Hostnames the lookup must never cite. Applied after `allowedDomains`. */
	@Column('json', { name: 'blockedDomains' })
	public blockedDomains!: Array<string>;

	/** USD cap for the lookup stage. Null uses the engine default. */
	@Column('decimal', {
		name: 'lookupSpendBudget',
		precision: 10,
		scale: 4,
		nullable: true,
	})
	public lookupSpendBudget!: number | null;

	// === Compiled engine artifacts (outputs of compile, never user input) ===

	/**
	 * Column name carries a double underscore because the referenced table is
	 * the legacy `_automation_flow` (separator + the table's own prefix), which
	 * is the same shape `marketing_campaign` uses.
	 */
	@Column('int', {
		name: 'fk__automation_flow__id',
		unsigned: true,
		nullable: true,
	})
	public fkAutomationFlowId!: number | null;

	@Column('int', {
		name: 'fk__automation_trigger__id',
		unsigned: true,
		nullable: true,
	})
	public fkAutomationTriggerId!: number | null;

	@ManyToOne(() => AutomationFlowEntity, { nullable: true })
	@JoinColumn({ name: 'fk__automation_flow__id' })
	public automationFlow?: AutomationFlowEntity | null;

	@ManyToOne(() => AutomationTriggerEntity, { nullable: true })
	@JoinColumn({ name: 'fk__automation_trigger__id' })
	public automationTrigger?: AutomationTriggerEntity | null;
}
